// src/App.js
import React, { useState, useEffect, useMemo, Suspense, lazy } from 'react';
import { Toaster } from 'react-hot-toast';
import { Home, ShoppingCart, Star, FileText, BookOpen, Shield, Target, DollarSign, Settings, Menu } from 'lucide-react';

import { useAuth } from './context/AuthContext';
import { useImages } from './context/ImageContext';
import { useFinancialData } from './context/FinancialContext';
import { useSocialData } from './context/SocialContext';
import { useFirebaseImage } from './hooks/useFirebaseImage';

import Loading from './components/ui/Loading';
import ErrorMessage from './components/ui/ErrorMessage';
import ErrorBoundary from './utils/ErrorBoundary';
import { NavigationTop, MobileMenu } from './components/layout/MobileMenu';
import { WelcomePanel } from './components/dashboard/WelcomePanel';
import ChangePasswordModal from './components/modals/ChangePasswordModal';
import TermsModal from './components/modals/TermsModal';
import { FALLBACK_LOGO_URL } from './utils/constants';

// Views (Lazy Load)
const LoginView = lazy(() => import('./views/LoginView').then(m => ({ default: m.LoginView })));
const InicialView = lazy(() => import('./views/InicialView').then(m => ({ default: m.InicialView })));
const ShopView = lazy(() => import('./views/ShopView').then(m => ({ default: m.ShopView })));
const ConfigurationView = lazy(() => import('./views/ConfigurationView'));
const FinancialView = lazy(() => import('./views/FinancialView'));
const ExtractView = lazy(() => import('./views/ExtractView'));
const GuideView = lazy(() => import('./views/GuideView'));
const RulesView = lazy(() => import('./views/RulesView'));
const SuggestionsView = lazy(() => import('./views/SuggestionsView'));
const PointsManagementView = lazy(() => import('./views/PointsManagementView'));

const Header = ({ onMenuToggle, logoUrl }) => { 
    const logoSrc = logoUrl || FALLBACK_LOGO_URL;
    return (
        <header className="w-full bg-[#1B2B6B] z-30 relative border-b-2 border-[#C0181C]">
            <div className="w-full h-14 md:h-20 relative flex items-center justify-center overflow-hidden">
                <img 
                    src={logoSrc} 
                    alt="Banner Institucional" 
                    className="h-full w-full object-cover object-center" 
                    onError={(e) => { e.target.onerror = null; e.target.src = FALLBACK_LOGO_URL; }}
                />
                <div className='absolute top-3 right-4 md:hidden'>
                    <button onClick={onMenuToggle} className="p-2 rounded-md bg-white/10 text-white shadow-sm cursor-pointer hover:bg-white/20 transition-colors">
                        <Menu className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </header>
    );
};

function App() {
    const [activeView, setActiveView] = useState(() => localStorage.getItem('lastView') || 'inicial');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isAcceptingTerms, setIsAcceptingTerms] = useState(false);

    const { isAuthenticated, isLoading: authLoading, error: authError, userProfile, userRole, termsAccepted, termsConfig, termsConfigLoading, handleLogout, handleLogin, handleAcceptTerms } = useAuth();
    const { logo, loginBg, generalBg } = useImages();
    const { transactions } = useFinancialData();
    const { suggestionsList } = useSocialData();

    // Contadores de pendências para badges na navegação (apenas para admin/gestor)
    const pendingBadges = useMemo(() => {
        const role = Number(userRole);
        if (role < 2) return {};
        return {
            suggestions: suggestionsList?.filter(s => s.status === 'pending').length || 0,
            config: (
                (transactions?.filter(t => t.type === 'kudos_request' && t.status === 'pending').length || 0) +
                (transactions?.filter(t => t.type === 'redemption' && t.deliveryStatus === 'pending').length || 0)
            ),
        };
    }, [userRole, transactions, suggestionsList]);

    const faviconUrl = useFirebaseImage('RPImagens/Favicon.jpg', null);

    useEffect(() => {
        if (faviconUrl) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement('link');
                link.rel = 'icon';
                document.head.appendChild(link);
            }
            link.href = faviconUrl;
        }
    }, [faviconUrl]);

    useEffect(() => {
        if (isAuthenticated) localStorage.setItem('lastView', activeView);
    }, [activeView, isAuthenticated]);

    useEffect(() => {
        if (!isAuthenticated) setActiveView('inicial');
    }, [isAuthenticated]);

    // Permite que qualquer componente interno navegue entre abas via evento
    useEffect(() => {
        const handler = (e) => { if (isAuthenticated) setActiveView(e.detail); };
        window.addEventListener('navigateTo', handler);
        return () => window.removeEventListener('navigateTo', handler);
    }, [isAuthenticated]);

    const views = useMemo(() => ({
        login: <LoginView onAuthenticate={handleLogin} backgroundUrl={loginBg} logoUrl={logo} />,
        inicial: <InicialView />,
        store: <ShopView />,
        suggestions: <SuggestionsView userId={userProfile?.id} />,
        extract: <ExtractView />,
        guide: <GuideView />,
        rules: <RulesView />,
        'points-manage': <PointsManagementView />,
        financial: <FinancialView userRole={userRole} />, 
        config: <ConfigurationView />
    }), [handleLogin, loginBg, logo, userProfile, userRole]);

    const navItems = useMemo(() => {
        const safeRole = userRole ? Number(userRole) : 1;
        const allItems = [
            { id: 'inicial',       label: 'Início',         icon: Home,         roles: [1, 2, 3] },
            { id: 'store',         label: 'Lojinha',        icon: ShoppingCart, roles: [1, 2, 3] },
            { id: 'suggestions',   label: 'Sugestões',      icon: Star,         roles: [1, 2, 3], badge: pendingBadges.suggestions },
            { id: 'extract',       label: 'Extrato',        icon: FileText,     roles: [1, 2, 3] },
            { id: 'guide',         label: 'Guia',           icon: BookOpen,     roles: [1, 2, 3] },
            { id: 'rules',         label: 'Regras',         icon: Shield,       roles: [1, 2, 3] },
            { id: 'points-manage', label: 'Pontos',         icon: Target,       roles: [2, 3] },
            { id: 'financial',     label: 'Financeiro',     icon: DollarSign,   roles: [3] },
            { id: 'config',        label: 'Configurações',  icon: Settings,     roles: [3], badge: pendingBadges.config },
        ];
        return allItems.filter(item => item.roles.includes(safeRole));
    }, [userRole, pendingBadges]);

    if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-[#1B2B6B]"><Loading text="Iniciando..." /></div>;
    if (authError && !isAuthenticated) return <div className="min-h-screen flex items-center justify-center p-4 bg-[#1B2B6B]"><ErrorMessage error={authError} /></div>;

    if (!isAuthenticated) {
        return (
            <Suspense fallback={<Loading />}>
                <div className="min-h-screen font-sans" style={{ backgroundImage: `url(${loginBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    {views.login}
                </div>
            </Suspense>
        );
    }

    // Aguarda config dos termos carregar — evita flash do app antes do modal
    if (termsConfigLoading) return <div className="min-h-screen flex items-center justify-center bg-[#1B2B6B]"><Loading text="Carregando..." /></div>;

    // Termos não aceitos: mostra APENAS o modal — nenhum conteúdo do app é visível
    if (!termsAccepted) {
        return (
            <TermsModal
                onAccept={async () => {
                    setIsAcceptingTerms(true);
                    try { await handleAcceptTerms(); } finally { setIsAcceptingTerms(false); }
                }}
                isLoading={isAcceptingTerms}
                termsVersion={termsConfig?.version}
            />
        );
    }

    return (
        // 🟢 MUDANÇA: Fundo Azul Base + Imagem de Fundo por cima
        <div
            className="min-h-screen flex flex-col font-sans transition-all duration-300 bg-[#1B2B6B]"
            style={{ 
                backgroundImage: generalBg ? `url(${generalBg})` : 'none', 
                backgroundAttachment: 'fixed', 
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            <Toaster position="top-right" toastOptions={{ style: { background: '#333', color: '#fff' } }} />
            
            <div className="sticky top-0 z-50 w-full shadow-sm bg-white">
                <Header onMenuToggle={() => setIsMenuOpen(true)} logoUrl={logo} />
                
                {/* 🟢 MUDANÇA: Container do Menu sem padding/margin extra que cria linhas brancas */}
                <div className="bg-white border-none m-0 p-0 block">
                    <NavigationTop activeView={activeView} onNavigate={setActiveView} navItems={navItems} />
                </div>
            </div>

            <main className="flex-1 p-4 md:p-6 pb-28 md:pb-6 relative z-10 max-w-7xl mx-auto w-full">
                <WelcomePanel userName={userProfile?.name} onLogout={handleLogout} userProfile={userProfile} onOpenPasswordModal={() => setIsPasswordModalOpen(true)} />
                
                <div className="animate-fade-in mt-6">
                    <ErrorBoundary compact>
                        <Suspense fallback={<div className="p-10 text-white"><Loading text="Carregando..." /></div>}>
                            {views[activeView] || views.inicial}
                        </Suspense>
                    </ErrorBoundary>
                </div>
            </main>

            <MobileMenu activeView={activeView} onNavigate={setActiveView} navItems={navItems} isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
            <ChangePasswordModal isOpen={isPasswordModalOpen} onClose={() => setIsPasswordModalOpen(false)} onChangePassword={() => {}} />
        </div>
    );
}

export default App;
